import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, 
  Search, 
  Filter, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Activity,
  BarChart3,
  Crown,
  Loader
} from 'lucide-react';

const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [cities, setCities] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  
  const [filters, setFilters] = useState({
    search: '',
    city: '',
    gender: '',
    membership: ''
  });

  const [debouncedSearch, setDebouncedSearch] = useState('');
  const observer = useRef();
  const searchTimeoutRef = useRef();

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    first_name: '',
    last_name: '',
    city: '',
    gender: '',
    phone: '',
    date_of_birth: '',
    membership_type: 'free',
    password: ''
  });

  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [filters.search]);

  // Reset pagination when filters change
  useEffect(() => {
    setUsers([]);
    setPagination(prev => ({ ...prev, page: 1 }));
    setHasMore(true);
    fetchUsers(true);
  }, [debouncedSearch, filters.city, filters.gender, filters.membership]);

  useEffect(() => {
    fetchCities();
  }, []);

  const fetchUsers = async (reset = false) => {
    try {
      const currentPage = reset ? 1 : pagination.page;
      
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const params = new URLSearchParams({
        page: currentPage,
        limit: pagination.limit,
        search: debouncedSearch,
        city: filters.city,
        gender: filters.gender,
        membership: filters.membership
      });
      
      const response = await axios.get(`/users?${params}`);
      
      // Safely handle the response
      if (response.data && Array.isArray(response.data.users)) {
        const newUsers = response.data.users;
        
        if (reset) {
          setUsers(newUsers);
        } else {
          setUsers(prevUsers => [...prevUsers, ...newUsers]);
        }
        
        setPagination({
          page: currentPage,
          limit: response.data.limit || 20,
          total: response.data.total || 0,
          totalPages: response.data.totalPages || 1
        });
        
        // Check if there are more pages
        setHasMore(currentPage < (response.data.totalPages || 1));
        
      } else if (response.data && Array.isArray(response.data)) {
        if (reset) {
          setUsers(response.data);
        } else {
          setUsers(prevUsers => [...prevUsers, ...response.data]);
        }
        setHasMore(false);
      } else {
        console.warn('Unexpected API response structure:', response.data);
        if (reset) {
          setUsers([]);
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      if (reset) {
        setUsers([]);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await axios.get('/cities');
      if (response.data && Array.isArray(response.data)) {
        setCities(response.data);
      } else {
        setCities([]);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      setCities([]);
    }
  };

  const viewUserStats = (userId) => {
    navigate(`/users/${userId}/stats`);
  };

  const fetchUserDetails = async (userId) => {
    try {
      const response = await axios.get(`/users/${userId}`);
      setSelectedUser(response.data);
      setShowUserModal(true);
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/users', newUser);
      setShowEditModal(false);
      setNewUser({
        name: '',
        email: '',
        first_name: '',
        last_name: '',
        city: '',
        gender: '',
        phone: '',
        date_of_birth: '',
        membership_type: 'free',
        password: ''
      });
      // Reset and fetch users
      setUsers([]);
      setPagination(prev => ({ ...prev, page: 1 }));
      setHasMore(true);
      fetchUsers(true);
    } catch (error) {
      console.error('Error creating user:', error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await axios.delete(`/users/${userId}`);
        // Remove user from current list
        setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
        // Update pagination total
        setPagination(prev => ({ ...prev, total: prev.total - 1 }));
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      city: '',
      gender: '',
      membership: ''
    });
  };

  const loadMoreUsers = useCallback(() => {
    if (hasMore && !loadingMore) {
      setPagination(prev => ({ ...prev, page: prev.page + 1 }));
      fetchUsers(false);
    }
  }, [hasMore, loadingMore, pagination.page]);

  // Infinite scroll observer
  const lastUserElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadingMore) {
        loadMoreUsers();
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore, loadingMore, loadMoreUsers]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'N/A';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">User Management</h2>
          <p className="text-gray-600 mt-2">Manage and monitor user accounts with detailed analytics</p>
        </div>
{/* <button
          onClick={() => setShowEditModal(true)}
          className="btn-primary flex items-center"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add User
        </button> */}
      </div>

      {/* Enhanced Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          
          <select
            value={filters.city}
            onChange={(e) => handleFilterChange('city', e.target.value)}
            className="input-field"
          >
            <option value="">All Cities</option>
            {cities.map((city) => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>
          
          <select
            value={filters.gender}
            onChange={(e) => handleFilterChange('gender', e.target.value)}
            className="input-field"
          >
            <option value="">All Genders</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
          
          <select
            value={filters.membership}
            onChange={(e) => handleFilterChange('membership', e.target.value)}
            className="input-field"
          >
            <option value="">All Memberships</option>
            <option value="free">Free</option>
            <option value="premium">Premium</option>
          </select>
        </div>
        
        {(filters.search || filters.city || filters.gender || filters.membership) && (
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Filter className="h-4 w-4" />
              <span>Filters applied</span>
              {pagination.total > 0 && <span>• {pagination.total} results</span>}
            </div>
            <button
              onClick={clearFilters}
              className="btn-secondary text-sm flex items-center"
            >
              <X className="h-4 w-4 mr-1" />
              Clear All
            </button>
          </div>
        )}
      </div>

      {/* Users Table with Infinite Scroll */}
      <div className="card">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : !users || users.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {filters.search || filters.city || filters.gender || filters.membership
                ? 'Try adjusting your filters to see more users.'
                : 'Get started by adding a new user.'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="table-header">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location & Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Activity Stats
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Membership
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joined
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user, index) => {
                    const isLast = users.length === index + 1;
                    return (
                      <tr 
                        key={user.id} 
                        ref={isLast ? lastUserElementRef : null}
                        className="table-row cursor-pointer hover:bg-gray-50" 
                        onClick={() => viewUserStats(user.id)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-12 w-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                              <span className="text-white font-medium text-lg">
                                {(user.name || user.first_name || 'U').charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user.name || `${user.first_name} ${user.last_name}`}
                              </div>
                              <div className="text-sm text-gray-500">{user.email}</div>
                              {user.phone && (
                                <div className="text-xs text-gray-400">{user.phone}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                            {user.city}
                          </div>
                          <div className="text-sm text-gray-500 capitalize">{user.gender}</div>
                          {user.date_of_birth && (
                            <div className="text-xs text-gray-400">Age: {calculateAge(user.date_of_birth)}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {user.total_steps?.toLocaleString() || 0} steps
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.total_distance?.toFixed(1) || 0} km • {user.activity_count || 0} activities
                          </div>
                          <div className="text-xs text-gray-400">
                            {user.total_calories?.toLocaleString() || 0} calories
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full ${
                            user.membership_type === 'premium' 
                              ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.membership_type === 'premium' && <Crown className="h-3 w-3 mr-1" />}
                            {user.membership_type || 'Free'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(user.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              viewUserStats(user.id);
                            }}
                            className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Stats"
                          >
                            <BarChart3 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              fetchUserDetails(user.id);
                            }}
                            className="text-green-600 hover:text-green-900 p-2 hover:bg-green-50 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteUser(user.id);
                            }}
                            className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete User"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Loading More Indicator */}
            {loadingMore && (
              <div className="flex items-center justify-center py-8">
                <Loader className="h-6 w-6 animate-spin text-blue-600 mr-2" />
                <span className="text-gray-600">Loading more users...</span>
              </div>
            )}

            {/* End of Results Indicator */}
            {!hasMore && users.length > 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">You've reached the end of the user list</p>
                <p className="text-sm text-gray-400 mt-1">
                  Showing all {pagination.total} users
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">User Details</h3>
                <button
                  onClick={() => setShowUserModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Personal Information */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900">Personal Information</h4>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <User className="h-5 w-5 text-gray-400" />
                      <span className="text-sm">{selectedUser.name}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Mail className="h-5 w-5 text-gray-400" />
                      <span className="text-sm">{selectedUser.email}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Phone className="h-5 w-5 text-gray-400" />
                      <span className="text-sm">{selectedUser.phone || 'Not provided'}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <MapPin className="h-5 w-5 text-gray-400" />
                      <span className="text-sm">{selectedUser.city}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Calendar className="h-5 w-5 text-gray-400" />
                      <span className="text-sm">
                        {selectedUser.date_of_birth 
                          ? formatDate(selectedUser.date_of_birth)
                          : 'Not provided'
                        }
                      </span>
                    </div>
                  </div>
                </div>

                {/* Activity Statistics */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900">Activity Statistics</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">
                        {selectedUser.total_steps?.toLocaleString() || 0}
                      </div>
                      <div className="text-sm text-gray-600">Total Steps</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {selectedUser.total_distance?.toFixed(1) || 0}
                      </div>
                      <div className="text-sm text-gray-600">Distance (km)</div>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-orange-600">
                        {selectedUser.total_calories?.toLocaleString() || 0}
                      </div>
                      <div className="text-sm text-gray-600">Calories</div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">
                        {selectedUser.activity_count || 0}
                      </div>
                      <div className="text-sm text-gray-600">Activities</div>
                    </div>
                  </div>
                  <div className="pt-4">
                    <button
                      onClick={() => {
                        setShowUserModal(false);
                        viewUserStats(selectedUser.id);
                      }}
                      className="btn-primary w-full flex items-center justify-center"
                    >
                      <BarChart3 className="h-5 w-5 mr-2" />
                      View Detailed Analytics
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Add New User</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={newUser.first_name}
                      onChange={(e) => setNewUser(prev => ({ ...prev, first_name: e.target.value }))}
                      className="input-field"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={newUser.last_name}
                      onChange={(e) => setNewUser(prev => ({ ...prev, last_name: e.target.value }))}
                      className="input-field"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={newUser.name}
                    onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                    className="input-field"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City
                    </label>
                    <select
                      value={newUser.city}
                      onChange={(e) => setNewUser(prev => ({ ...prev, city: e.target.value }))}
                      className="input-field"
                    >
                      <option value="">Select City</option>
                      {cities.map((city) => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Gender
                    </label>
                    <select
                      value={newUser.gender}
                      onChange={(e) => setNewUser(prev => ({ ...prev, gender: e.target.value }))}
                      className="input-field"
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={newUser.phone}
                      onChange={(e) => setNewUser(prev => ({ ...prev, phone: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      value={newUser.date_of_birth}
                      onChange={(e) => setNewUser(prev => ({ ...prev, date_of_birth: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Membership Type
                  </label>
                  <select
                    value={newUser.membership_type}
                    onChange={(e) => setNewUser(prev => ({ ...prev, membership_type: e.target.value }))}
                    className="input-field"
                  >
                    <option value="free">Free</option>
                    <option value="premium">Premium</option>
                  </select>
                </div>

                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                  >
                    Create User
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement; 